'use client';

import { c as _c } from "react/compiler-runtime";
import { useConvertCustomMarkerCoordinatesMutation, useGetCustomMarkersQuery, useGetFoundPinsQuery } from '@repo/store/maps/api/mapsApi';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectExternalMapId, selectIsInCreatorMode, selectPlayerLocationDeltas } from '@repo/store/maps/slices/mapData';
import type { PlayerLocationDeltas } from '@repo/types/maps/map-types';
import { useEffect, useMemo, useRef } from 'react';
function hasValidConversionDeltas(deltas: {
  scaleX?: number;
  scaleY?: number;
}): boolean {
  const scaleX = deltas.scaleX ?? 1;
  const scaleY = deltas.scaleY ?? 1;
  return Number.isFinite(scaleX) && Number.isFinite(scaleY) && scaleX !== 0 && scaleY !== 0;
}
export function useGameMapUserData() {
  const $ = _c(12);
  const isAuthenticated = useAppSelector(_temp);
  const externalMapId = useAppSelector(selectExternalMapId);
  const isInCreatorMode = useAppSelector(selectIsInCreatorMode);
  const playerLocationDeltas = useAppSelector(selectPlayerLocationDeltas);
  const deltas = playerLocationDeltas;
  const t0 = !isAuthenticated || !externalMapId || isInCreatorMode;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      skip: t0,
      refetchOnMountOrArgChange: true
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useGetFoundPinsQuery(externalMapId ?? "", t1);
  const t2 = !isAuthenticated || !externalMapId || isInCreatorMode;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      skip: t2,
      refetchOnMountOrArgChange: true
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data: customMarkers
  } = useGetCustomMarkersQuery(externalMapId ?? "", t3);
  const [convertCustomMarkerCoordinates] = useConvertCustomMarkerCoordinatesMutation();
  const isConvertingRef = useRef(false);
  let t4;
  let t5;
  if ($[4] !== convertCustomMarkerCoordinates || $[5] !== customMarkers || $[6] !== deltas || $[7] !== externalMapId || $[8] !== isAuthenticated) {
    t4 = () => {
      if (!customMarkers || !deltas || !externalMapId || !isAuthenticated) {
        return;
      }
      if (!deltas.deltas || !hasValidConversionDeltas(deltas.deltas)) {
        return;
      }
      if (isConvertingRef.current) {
        return;
      }
      const markersNeedingConversion = customMarkers.filter(_temp2);
      if (markersNeedingConversion.length === 0) {
        return;
      }
      isConvertingRef.current = true;
      const playerLocationDeltasObj = {
        referencePoints: deltas.referencePoints || [],
        deltas: deltas.deltas
      };
      const promises = markersNeedingConversion.map(marker_0 => convertCustomMarkerCoordinates({
        markerId: marker_0.properties.externalId,
        externalId: externalMapId,
        playerLocationDeltas: playerLocationDeltasObj
      }).unwrap().catch(_temp3));
      Promise.all(promises).finally(() => {
        isConvertingRef.current = false;
      });
    };
    t5 = [customMarkers, deltas, externalMapId, isAuthenticated, convertCustomMarkerCoordinates];
    $[4] = convertCustomMarkerCoordinates;
    $[5] = customMarkers;
    $[6] = deltas;
    $[7] = externalMapId;
    $[8] = isAuthenticated;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {};
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
function _temp3() {}
function _temp2(marker) {
  return marker.properties.gameCoords?.x && marker.properties.gameCoords.y && marker.properties.gameCoords.z && marker.geometry.coordinates[0] === 0 && marker.geometry.coordinates[1] === 0;
}
function _temp(state) {
  return state.auth.isAuthenticated;
}